export interface NavLink {
  label: string;
  path: string;
}

export interface HeroContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  primaryCta: {
    label: string;
    path: string;
  };
  secondaryCta: {
    label: string;
    path: string;
  };
}

export interface CoreTheme {
  title: string;
  description: string;
  icon: string;
}

export interface ServiceSummaryItem {
  title: string;
  description: string;
}

export interface ProcessStep {
  step: string;
  title: string;
  description: string;
}

export interface DemoItem {
  id: string;
  title: string;
  category: string;
  description: string;
  demoUrl?: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

export interface ServiceDetailItem {
  title: string;
  description: string;
  features?: string[];
}

export interface ClientContent {
  businessName: string;
  tagline: string;
  location: string;
  contactEmail: string;
  navLinks: NavLink[];
  seo: {
    home: { title: string; description: string; canonical: string };
    whatWeDo: { title: string; description: string; canonical: string };
    concepts: { title: string; description: string; canonical: string };
    faq: { title: string; description: string; canonical: string };
    about: { title: string; description: string; canonical: string };
    contact: { title: string; description: string; canonical: string };
  };
  home: {
    hero: HeroContent;
    audience: {
      heading: string;
      description: string;
      painPoints: string[];
    };
    themes: CoreTheme[];
    servicesSummary: ServiceSummaryItem[];
    process: ProcessStep[];
    featuredDemos: DemoItem[];
  };
  whatWeDo: {
    title: string;
    subtitle: string;
    services: ServiceDetailItem[];
    boundaries: {
      heading: string;
      description: string;
      supported: string[];
      notSupported: string[];
    };
  };
  concepts: {
    title: string;
    subtitle: string;
    notice: string;
    items: {
      id: string;
      title: string;
      category: string;
      description: string;
      styleNote: string;
      demoUrl: string;
    }[];
    supporting: {
      heading: string;
      description: string;
      ctaLabel: string;
      ctaPath: string;
    };
  };
  faq: {
    title: string;
    subtitle: string;
    categories: {
      category: string;
      items: FaqItem[];
    }[];
  };
  about: {
    title: string;
    subtitle: string;
    whoYouAre: {
      heading: string;
      name: string;
      role: string;
      experience: string;
      bioParagraphs: string[];
    };
    whyCreated: {
      heading: string;
      description: string;
    };
    philosophy: {
      heading: string;
      items: { title: string; description: string }[];
    };
    howWeWork: {
      heading: string;
      steps: ProcessStep[];
    };
  };
  contact: {
    title: string;
    subtitle: string;
    badge: string;
    footerNote: string;
  };
}

export const clientContent: ClientContent = {
  businessName: "Clockwork Web",
  tagline: "Professional websites for small businesses, without the hassle.",
  location: "Based in Surrey, serving businesses nationwide.",
  contactEmail: "info@clockworkweb.co.uk",
  navLinks: [
    { label: "Home", path: "/" },
    { label: "What We Do", path: "/what-we-do" },
    { label: "Concepts", path: "/concepts" },
    { label: "FAQ", path: "/faq" },
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ],
  seo: {
    home: {
      title: "Clockwork Web | Professional Websites for Small Businesses",
      description: "Professional, custom websites for small businesses and independent professionals in the UK. Fully managed hosting, setup, and ongoing support without the hassle.",
      canonical: "https://www.clockworkweb.co.uk/",
    },
    whatWeDo: {
      title: "What We Do | Clockwork Web",
      description: "Discover our fully managed web design, development, hosting, DNS management, and technical SEO services for small businesses.",
      canonical: "https://www.clockworkweb.co.uk/what-we-do",
    },
    concepts: {
      title: "Website Concepts | Clockwork Web",
      description: "Explore interactive demo website concepts built for independent professionals, trades, and small businesses.",
      canonical: "https://www.clockworkweb.co.uk/concepts",
    },
    faq: {
      title: "Frequently Asked Questions | Clockwork Web",
      description: "Common questions about our website design process, hosting, domain responsibilities, post-launch changes, and pricing.",
      canonical: "https://www.clockworkweb.co.uk/faq",
    },
    about: {
      title: "About Us | Clockwork Web",
      description: "Learn about Clockwork Web, founded by Nick Smith with over a decade of full-stack engineering expertise.",
      canonical: "https://www.clockworkweb.co.uk/about",
    },
    contact: {
      title: "Contact Us | Clockwork Web",
      description: "Get in touch with Clockwork Web to discuss a professional website for your small business or independent practice.",
      canonical: "https://www.clockworkweb.co.uk/contact",
    },
  },
  home: {
    hero: {
      eyebrow: "Professional Websites for Small Businesses",
      title: "Professional websites for small businesses",
      subtitle: "Built efficiently. Managed for you. We design and build custom websites for small businesses and independent professionals who want a great online presence without the technical hassle.",
      primaryCta: {
        label: "Get in Touch",
        path: "/contact",
      },
      secondaryCta: {
        label: "View Examples",
        path: "/examples",
      },
    },
    audience: {
      heading: "Built for small businesses and independent professionals",
      description: "Ideal for consultants, tradespeople, contractors, and service businesses with an outdated, sub-optimal, or non-existent website.",
      painPoints: [
        "No time to learn complicated website builders or navigate technical hosting issues",
        "Outdated or DIY site that doesn't reflect the true quality of your business",
        "Looking for a straightforward process without endless meetings or agency overhead",
        "Need reliable ongoing support and someone to handle updates",
      ],
    },
    themes: [
      {
        title: "Simple",
        description: "No complicated website builders, confusing jargon, or technical friction. A smooth process from start to finish.",
        icon: "zap",
      },
      {
        title: "Professional",
        description: "Clean, bespoke-looking websites with strong typography, excellent mobile design, and clear messaging.",
        icon: "award",
      },
      {
        title: "Managed",
        description: "We handle hosting, DNS, SSL security, and ongoing changes so you can focus entirely on your work.",
        icon: "shield",
      },
    ],
    servicesSummary: [
      {
        title: "Website Design & Development",
        description: "Custom, responsive websites crafted for desktop, tablet, and mobile devices.",
      },
      {
        title: "Hosting & Technical Management",
        description: "Secure hosting, DNS setup, HTTPS/SSL certificates, deployment and ongoing technical maintenance taken care of.",
      },
      {
        title: "SEO Foundations",
        description: "Built with clean semantic HTML, correct metadata, and strong technical on-page structure.",
      },
      {
        title: "Ongoing Post-Launch Support",
        description: "Content changes handled after launch without needing a complex CMS.",
      },
    ],
    process: [
      {
        step: "01",
        title: "Tell us what you need",
        description: "Send a brief message about your business and what you want your website to achieve.",
      },
      {
        step: "02",
        title: "We build your site",
        description: "We craft a professional, mobile-friendly website tailored specifically to your services.",
      },
      {
        step: "03",
        title: "Review & Refine",
        description: "Review the site preview and request any reasonable adjustments before launch.",
      },
      {
        step: "04",
        title: "Launch & Look After",
        description: "We handle DNS and hosting setup, launch your site, and take care of ongoing maintenance.",
      },
    ],
    featuredDemos: [
      {
        id: "trades",
        title: "Property Maintenance & Trades",
        category: "Trades & Services",
        description: "Clean, conversion-focused layout highlighting core services, credentials, and easy quote requests.",
      },
      {
        id: "consulting",
        title: "Independent Consultant & Advisor",
        category: "Consulting & Advisory",
        description: "Sophisticated, personal-led presence establishing expertise and encouraging direct enquiries.",
      },
      {
        id: "professional-services",
        title: "Small Business & Professional Services",
        category: "Professional Services",
        description: "Professional, trustworthy design presenting services clearly and making it easy for prospective customers to get in touch.",
      },
    ],
  },
  whatWeDo: {
    title: "What We Do",
    subtitle: "Website creation and ongoing technical management for small businesses and individuals.",
    services: [
      {
        title: "Website Design & Development",
        description: "We create bespoke-looking, responsive websites designed to present your business with clarity and professionalism. Every site is built lightweight and fast, ensuring an optimal experience on mobile, tablet, and desktop.",
        features: [
          "Custom layout tailored to your business",
          "Mobile-first responsive design",
          "Fast loading speeds without bulky builders",
          "Clear calls to action to drive enquiries",
        ],
      },
      {
        title: "Hosting & Technical Management",
        description: "We take the technical burden off your shoulders. From setting up secure hosting and domain DNS configuration to HTTPS/SSL encryption and redirects, we manage everything behind the scenes.",
        features: [
          "Reliable, high-performance web hosting",
          "DNS configuration support",
          "HTTPS / SSL security setup",
          "Ongoing technical maintenance",
        ],
      },
      {
        title: "SEO Foundations",
        description: "Your website is built with robust technical search engine foundations so potential customers can find you easily. We focus on clean code, semantic structure, and proper metadata.",
        features: [
          "Descriptive page titles and meta descriptions",
          "Canonical URLs",
          "Semantic HTML and logical heading hierarchy",
          "Optimised for fast indexing",
        ],
      },
      {
        title: "Ongoing Changes",
        description: "Websites shouldn't be static headaches. After launch, you can request reasonable content updates and text changes without needing to learn a complex CMS or plugin dashboard.",
        features: [
          "Small content updates handled for you",
          "No complex dashboard to maintain",
          "Direct communication with your developer",
          "Quote-based major additions when required",
        ],
      },
    ],
    boundaries: {
      heading: "What our service is and is not suited for",
      description: "To keep our service fast, high-quality, and stress-free, we focus specifically on professional business websites rather than complex web applications.",
      supported: [
        "Small business websites",
        "Service-based independent professionals",
        "Trades, consultants, and contractors",
        "Informational and portfolio sites",
      ],
      notSupported: [
        "Ecommerce shopping carts & payment gateways",
        "Complex booking systems & member portals",
        "User account dashboards & forums",
        "Extensive self-managed CMS platforms",
      ],
    },
  },
  concepts: {
    title: "Website Concepts",
    subtitle: "A few examples of what we could create for different types of small business.",
    notice: "These are demonstration websites created to show different design styles and approaches. They are concept websites, not client projects.",
    items: [
      {
        id: "electrician",
        title: "Independent Electrician",
        category: "Trades & Services",
        description: "A clean, trustworthy website focused on services, credibility and generating enquiries from local customers.",
        styleNote: "Clean, practical and trustworthy",
        demoUrl: "https://electrician.clockworkweb.co.uk",
      },
      {
        id: "counsellor",
        title: "Independent Counsellor",
        category: "Health & Well-being",
        description: "A calm, reassuring website for an independent counsellor, designed to build trust, communicate privacy and approachability, and encourage confidential client enquiries.",
        styleNote: "Calm, reassuring and empathetic",
        demoUrl: "https://counsellor.clockworkweb.co.uk",
      },
      {
        id: "law-firm",
        title: "Small Law Firm",
        category: "Professional Services",
        description: "A confident, sophisticated website designed to communicate expertise and trust without feeling like a large corporate firm.",
        styleNote: "Bold, confident and sophisticated",
        demoUrl: "https://lawfirm.clockworkweb.co.uk",
      },
    ],
    supporting: {
      heading: "Your website would be built around your business",
      description: "These are not fixed templates. We use proven design systems and components to build websites efficiently, then tailor the design, content and structure around each business.",
      ctaLabel: "Tell Us About Your Business",
      ctaPath: "/contact",
    },
  },
  faq: {
    title: "Frequently Asked Questions",
    subtitle: "Straightforward answers to the questions you're likely to have before starting.",
    categories: [
      {
        category: "Getting started",
        items: [
          {
            question: "How much does a website cost?",
            answer: "This depends on the work involved, but our prices are very affordable for small businesses. Get in touch with a few details about your project, and we'll provide a clear quote with no hidden fees.",
          },
          {
            question: "How long does it take?",
            answer: "Most websites are designed, built, and launched within 1 to 2 weeks once we have your initial business details and content.",
          },
          {
            question: "What do you need from me?",
            answer: "Just a brief description of what your business does, your core services, your preferred contact details, and any logos or photos you already have. We'll send you a questionnaire about your business, and the more details you provide, the better we can build your website.",
          },
          {
            question: "Do I need to provide the text and images?",
            answer: "If you have existing text and photos, great! If not, don't worry—we can help structure your messaging and source appropriate professional visuals or placeholders to get your site looking polished.",
          },
          {
            question: "Do you write the content?",
            answer: "We help outline, refine, and structure your service pages based on what your customers need to know. We work with your core business details to ensure professional, clear copywriting.",
          },
        ],
      },
      {
        category: "Website",
        items: [
          {
            question: "How many pages can I have?",
            answer: "Generally speaking, small business websites typically only need 5 or slightly more pages (Home, About, Services, Examples, and Contact). You can of course have as many pages as you like and you know your business best.",
          },
          {
            question: "Will it work on mobile?",
            answer: "Absolutely. Nearly half of web users browse on a mobile device, so if your website isn’t easy to use on a phone, you could be losing many potential customers. Every website we build is mobile-first, so it looks and works properly on all devices.",
          },
          {
            question: "Will my website be SEO-friendly?",
            answer: "Yes. Every site is built with robust technical SEO foundations, including clean semantic HTML, fast loading speeds, proper meta titles, descriptions, and canonical links. We are not an SEO agency, but we believe your SEO efforts should focus on the area where you primarily operate.",
          },
          // {
          //   question: "Can you add a contact form?",
          //   answer: "Yes. We include clean, reliable contact channels and enquiry forms so potential customers can reach out to you directly with zero friction.",
          // },
          {
            question: "Can you add specific functionality?",
            answer: "We focus on professional business websites. Your website will likely not need anything complex, but just ask us and we can discuss any such requirements—though we avoid complex systems like ecommerce stores or member portals to keep your site fast, reliable and simple.",
          },
          {
            question: "Can I see examples?",
            answer: "Yes! You can explore our demo examples page to see representative layouts built for various trades, consultants, and service businesses.",
          },
        ],
      },
      {
        category: "Hosting & domain",
        items: [
          {
            question: "What is hosting, and why do I need it?",
            answer: "Building website files is only the first step—hosting is what actually puts your website on the internet so customers around the world can access it 24/7. We provide high-performance, secure hosting as part of our managed service so you never have to worry about server setup or technical configuration.",
          },
          {
            question: "Where is my website hosted?",
            answer: "Your website is hosted on high-performance, reliable global infrastructure optimised for speed and security, managed entirely by us.",
          },
          {
            question: "Do I own my domain?",
            answer: "Yes, you always retain 100% ownership of your domain name (e.g. yourbusiness.co.uk). It remains registered in your name. You'll just need to point the DNS entries to our server so that we can serve the website files to your domain, which we can help you with.",
          },
          {
            question: "Can I keep my existing domain?",
            answer: "Yes. If you already own a domain name, we simply point it to your new website without any disruption.",
          },
          {
            question: "What about email?",
            answer: "If you have an existing email provider (such as Microsoft 365, Google Workspace, or your domain registrar email), it'll remain completely untouched and continue to work normally.",
          },
          {
            question: "Do you provide SSL?",
            answer: "Yes. Every website includes a secure HTTPS/SSL certificate setup and automatic renewal, ensuring visitor trust and data encryption.",
          },
          {
            question: "What happens if I stop using your service?",
            answer: "Our service has two parts: building your initial website, and our ongoing managed service (hosting, security, and any content updates). Once your website is built and paid for, you own your content and code. If you ever decide to stop using our ongoing monthly service, we can provide you with your completed website files upon request (if you haven't already after initial completion). Please note that you will then need to arrange your own web hosting, set up your own HTTPS/SSL certificate, handle renewals, and handle future technical management yourself in order to keep your website served on the internet.",
          },
        ],
      },
      {
        category: "Changes & maintenance",
        items: [
          {
            question: "What if I need to change something later?",
            answer: "Simply send us an email with your proposed content changes, and we take care of it.",
          },
          {
            question: "How much do changes cost?",
            answer: "Small content and image updates are charged at our standard rate of £60 per hour. Larger changes, new pages or new functionality will be quoted separately.",
          },
          {
            question: "Who maintains the website?",
            answer: "We do. We handle hosting, security updates, SSL renewals, technical maintenance, and content changes so you can focus entirely on running your business.",
          },
        ],
      },
      {
        category: "General",
        items: [
          // {
          //   question: "Why shouldn't I just use a CMS?",
          //   answer: "You can of course create a website by yourself, but with DIY builders, you have to spend time researching which platform to use and learning how to use them. They also come with expensive monthly subscription fees, laggy code, and ongoing self-management. We build your site properly from scratch and take care of everything for you.",
          // },
          {
            question: "Can you replace my existing website?",
            answer: "Yes. If your current website is outdated, slow, or doesn't reflect the quality of your business, we can replace it with a modern, fast, mobile-friendly design.",
          },
          {
            question: "Do you work with businesses outside the UK?",
            answer: "We primarily work with UK-based small businesses and independent professionals, but we are happy to discuss projects for businesses elsewhere.",
          },
          {
            question: "Do I own my website?",
            answer: "You own your domain and content, and you have full rights to your business presence. We can send you all of your website files after completion upon request. Our managed model provides you with a high-performance, fully maintained web presence without the headaches of server administration.",
          },
        ],
      },
    ],
  },
  about: {
    title: "About Clockwork Web",
    subtitle: "Professional web development expertise, delivered without agency bureaucracy.",
    whoYouAre: {
      heading: "Who is behind the business",
      name: "Nick Smith",
      role: "Founder & Lead Full-Stack Engineer",
      experience: "Over a decade of professional web development and software experience",
      bioParagraphs: [
        "Clockwork Web was founded by Nick Smith, a Lead Full-Stack Software Engineer with over a decade of professional software development experience specialising in modern web architecture, fast websites, and React applications. He has built multiple advanced software applications from scratch, but wants to focus on simpler websites that make a difference to those who are less technical.",
        "After seeing countless small business owners struggle with bloated website builders, confusing technical jargon, laggy CMS's, and expensive digital agencies, he wanted to build a straightforward alternative that cuts out the friction completely.",
      ],
    },
    whyCreated: {
      heading: "Our view",
      description: "Small businesses shouldn't need to become experts in websites just to have a professional online presence. You run a business; you shouldn't have to worry about DNS records, hosting configurations, security certificates, or learning a CMS. We take care of all the technical weight so you don't have to.",
    },
    philosophy: {
      heading: "Our philosophy",
      items: [
        {
          title: "Keep things simple",
          description: "Avoid unnecessary complexity, bloated plugins, and confusing technical jargon.",
        },
        {
          title: "Do things properly",
          description: "Deliver fast, accessible, mobile-first websites using modern, lightweight engineering standards.",
        },
        {
          title: "No unnecessary complexity",
          description: "Focus purely on what works and what your business actually needs to succeed online.",
        },
        {
          title: "No bloated software",
          description: "Clean, bespoke code instead of fragile page builders or heavy CMS platforms.",
        },
        {
          title: "No confusing processes",
          description: "Direct, transparent communication and a smooth experience from start to finish.",
        },
        {
          title: "No excessive meetings",
          description: "Respect your time with efficient async collaboration and clear milestones.",
        },
      ],
    },
    howWeWork: {
      heading: "How we work",
      steps: [
        {
          step: "01",
          title: "You tell us what you need",
          description: "Send a brief message about your business and what you want your website to achieve.",
        },
        {
          step: "02",
          title: "We build it",
          description: "We craft a professional, mobile-friendly website tailored specifically to your services.",
        },
        {
          step: "03",
          title: "You review it",
          description: "Review the live site preview and request any reasonable adjustments before launch.",
        },
        {
          step: "04",
          title: "We launch it",
          description: "We handle domain routing, SSL security, and launch your site smoothly.",
        },
        {
          step: "05",
          title: "We look after it",
          description: "We manage ongoing hosting, technical maintenance, and content updates so you never have to worry.",
        },
      ],
    },
  },
  contact: {
    title: "Let's talk about your website.",
    subtitle: "We keep things straightforward. No complex forms or endless questionnaires—just drop us a line directly and we'll take it from there.",
    badge: "Get in Touch",
    footerNote: "Based in Surrey, UK — working with small businesses and independent professionals nationwide.",
  },
};
